/**
 * The app's only entry point for capturing the screen. A web page cannot take
 * a real screenshot, so today this re-renders the DOM; once the app ships as an
 * APK a native plugin replaces the implementation behind this same signature.
 *
 * The implementation is loaded on demand. modern-screenshot is ~23 KB that only
 * the Bildir flow ever uses, and a static import put it inside the single
 * bundle that every first paint has to download — including the first paint of
 * a visitor who never signs in.
 */

import { getAutoScreenshot } from '@/services/appPrefs';

/** Schedules work for the first idle moment, so it cannot delay first paint. */
function whenIdle(run: () => void): void {
  const w = window as typeof window & {
    requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
  };
  // Safari had no requestIdleCallback until 16.4; a timer is close enough here.
  if (w.requestIdleCallback) w.requestIdleCallback(run, { timeout: 3000 });
  else window.setTimeout(run, 1500);
}

export async function captureCurrentScreen(): Promise<File | null> {
  try {
    const { captureCurrentScreen: capture } = await import('./domCapture');
    return await capture();
  } catch {
    // Matches domCapture's own contract: a failed capture opens the report
    // without a picture rather than blocking the tap.
    return null;
  }
}

/** The picture a report carries: null when auto-screenshot is off or the capture failed. */
export async function captureForReport(): Promise<File | null> {
  return getAutoScreenshot() ? captureCurrentScreen() : null;
}

/**
 * Safari renders the first foreignObject capture blank, so a throwaway capture
 * is paid for up front. Now it also pays for the module download, which is why
 * this waits for idle instead of racing the first paint it exists to protect.
 */
export function warmUpCapture(): void {
  whenIdle(() => {
    void import('./domCapture').then((m) => m.warmUpCapture()).catch(() => {});
  });
}
